import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';
import UserDoctorRegularIcon from '../icons/user-doctor-regular';

function getTextClassname(version: TDefaultAvatarProps['version']) {
    if (version === 'small') {
        return 'Caption2Bold dr-text-darwin-green-primary';
    } else {
        return 'Title3Bold dr-text-darwin-green-primary';
    }
}

function getWrapperClassname(version: TDefaultAvatarProps['version']) {
    if (version === 'small') {
        return 'dr-w-24 dr-h-24 dr-rounded-circle dr-flex dr-items-center dr-justify-center dr-bg-darwin-green-bg-dark';
    } else {
        return 'dr-w-48 dr-h-48 dr-rounded-circle dr-flex dr-items-center dr-justify-center dr-bg-darwin-green-bg-dark';
    }
}

const TooltipComponent = ({
    title,
    children,
    arrowPointAtCenter = false,
    placement = 'topLeft',
    textTransform,
}: {
    title: string;
    children: React.ReactNode;
    arrowPointAtCenter?: boolean;
    placement?: TooltipPlacement;
    textTransform?: React.CSSProperties['textTransform'];
}): JSX.Element => {
    return (
        <Tooltip
            placement={placement}
            title={title}
            destroyTooltipOnHide={true}
            arrowPointAtCenter={arrowPointAtCenter}
            overlayInnerStyle={{
                textTransform: textTransform,
                backgroundColor: '#394452',
                borderRadius: 4,
                fontFamily: 'lato',
                fontSize: 12,
                fontWeight: 400,
                padding: 8,
                // width: 12,
            }}
        >
            {children}
        </Tooltip>
    );
};

export interface TDefaultAvatarProps {
    name: string;
    version: 'small' | 'large';
}

export function DefaultAvatar({ name, version = 'small' }: TDefaultAvatarProps): JSX.Element {
    const wordsToShowOnNameChip = useMemo(() => {
        const nameSplits = name?.split(' ') || [];
        const stringToDisplay = nameSplits.reduce((prev, acc) => {
            if (prev.length < 2 && !!acc.trim()) {
                return `${prev}${acc[0]}`;
            }

            return prev;
        }, '');

        return stringToDisplay.toUpperCase();
    }, [name]);

    const textClassName = getTextClassname(version);
    const wrapperClassname = getWrapperClassname(version);

    return (
        <div className={wrapperClassname}>
            <p className={textClassName}>{wordsToShowOnNameChip}</p>
        </div>
    );
}

const DocAvatarUi = ({
    pic,
    name,
    isSelected = false,
    picType = 'large',
}: {
    pic?: string;
    name?: string;
    isSelected?: boolean;
    picType?: 'small' | 'large';
}): JSX.Element => {
    if (pic) {
        return (
            <img
                src={pic}
                className={`${
                    picType === 'large' ? 'dr-w-24 dr-h-24' : 'dr-w-16 dr-h-16'
                } dr-rounded-circle`}
                alt=""
            />
        );
    }
    if (name) {
        return <DefaultAvatar name={name} version="small" />;
    }
    return (
        <UserDoctorRegularIcon
            className={`dr-w-14 dr-h-14 ${
                isSelected ? 'dr-text-darwin-primary-primary' : 'dr-text-darwin-neutral-800'
            }`}
        />
    );
};

const DoctorFilterStripUi = ({
    doctorName,
    doctorPic,
    aptCount,
    isSelected = false,
    showAptCount = true,
    onClick,
}: {
    doctorName?: string;
    doctorPic?: string;
    isSelected?: boolean;
    showAptCount?: boolean;
    aptCount?: number;
    onClick: () => void;
}): JSX.Element => {
    // const commonStatusStyles = "dr-w-8 dr-h-8 dr-rounded-full";
    // const statusMapping = useMemo(
    //   () =>
    //     doctorStatusList.map((status) => {
    //       if (status === CHECKED_IN_STATUS.ON_GOING) {
    //         return (
    //           <div
    //             className={`${commonStatusStyles} dr-bg-darwin-green-primary`}
    //           />
    //         );
    //       } else if (status === CHECKED_IN_STATUS.UP_NEXT) {
    //         return (
    //           <div
    //             className={`${commonStatusStyles} dr-bg-darwin-yellow-primary`}
    //           />
    //         );
    //       }
    //       return (
    //         <div className={`${commonStatusStyles} dr-bg-darwin-neutral-200`} />
    //       );
    //     }),
    //   [doctorStatusList]
    // );

    return (
        <div
            id={`doctor-chip-${doctorName}`}
            onClick={onClick}
            className={`border-1 ${
                isSelected ? 'dr-border-darwin-primary-primary' : 'dr-border-darwin-neutral-200'
            } dr-py-6 dr-px-8 dr-items-center dr-bg-darwin-neutral-0 dr-rounded-8 dr-flex dr-flex-row dr-max-w-full dr-justify-between dr-cursor-pointer dr-min-w-160 dr-space-x-4 ${
                doctorName ? '' : 'dr-sticky dr-left-0'
            }`}
        >
            <DocAvatarUi name={doctorName} pic={doctorPic} isSelected={isSelected} />
            <div
                className={`${
                    isSelected
                        ? 'dr-text-darwin-primary-primary BodyBold'
                        : 'dr-text-darwin-neutral-1000 BodyRegular'
                } dr-ml-8 dr-mr-4 dr-flex-1 dr-flex dr-flex-row dr-items-center`}
            >
                <div className="dr-line-clamp-1">{doctorName || 'All doctors'}</div>
                {/* TODO: Pick color mapping */}
                {/* <div className="dr-flex dr-justify-center dr-items-center dr-space-x-4 dr-ml-8">
          {...statusMapping}
        </div> */}
            </div>
            {showAptCount ? (
                <div className="dr-flex dr-items-center dr-justify-center dr-rounded-40 dr-bg-darwin-primary-bg-light dr-text-darwin-primary-primary dr-w-24 dr-h-24">
                    {aptCount}
                </div>
            ) : (
                <></>
            )}
        </div>
    );
};

export default DoctorFilterStripUi;
